/**
 * VehicleLinksSection - Seção "Vínculos" do modal de edição de veículo
 *
 * Exibe os negócios e as tarefas atualmente vinculados ao veículo e permite
 * vincular/desvincular diretamente pelo formulário de edição.
 *
 * Regras importantes:
 *  - Só faz sentido em modo edição (o veículo precisa existir para ter vínculos).
 *  - Leitura via RPC `get_vehicle_details` (SECURITY DEFINER), que já aplica o
 *    gate de menu (has_granular_permission) e a visibilidade por registro.
 *  - Escrita via RPCs `link_deal_vehicle` / `link_task_vehicle` (hooks existentes).
 *  - As ações são aplicadas imediatamente (não dependem do botão "Salvar"),
 *    igual ao comportamento das abas de veículos do negócio e da tarefa.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-09 v1.0.0 - Criação inicial (vínculos no modal de edição)
 */

import { useMemo, useState } from 'react';
import { Briefcase, CheckSquare, Link2, Loader2, Unlink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { FormSection } from '@/components/common/FormSection';
import { InlineNotice } from '@/components/ui/inline-notice';
import { DealSelector } from '@/components/tasks/DealSelector';
import { ParentTaskSelector } from '@/components/tasks/ParentTaskSelector';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useVehicleDetails } from '@/hooks/useVehicleDetails';
import { useDealVehicleLink, useTaskVehicleLink } from '@/hooks/useCustomerVehicles';

/** Props da seção */
interface VehicleLinksSectionProps {
  /** Veículo em edição (null/undefined = criação, seção não é exibida) */
  vehicleId: string | null;
  /** Habilita as queries (modal aberto) */
  enabled?: boolean;
}

/** Formata valor em BRL (usado no resumo do negócio) */
function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function VehicleLinksSection({ vehicleId, enabled = true }: VehicleLinksSectionProps) {
  // Permissões de menu — evita exibir o bloco quando o usuário não acessa o módulo
  const { canView, isAdmin } = usePermissionsContext();
  const canViewDeals = isAdmin || canView('crm_deals');
  const canViewTasks = isAdmin || canView('tasks_list');

  const { deals, tasks, isLoading, refetch } = useVehicleDetails(
    vehicleId,
    enabled && !!vehicleId,
  );

  const dealLink = useDealVehicleLink();
  const taskLink = useTaskVehicleLink();

  // Seleção pendente nos seletores (limpa após vincular)
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const linkedDealIds = useMemo(() => new Set(deals.map((d) => d.id)), [deals]);
  const linkedTaskIds = useMemo(() => new Set(tasks.map((t) => t.id)), [tasks]);

  if (!vehicleId || (!canViewDeals && !canViewTasks)) return null;

  /** Vincula/desvincula um negócio e recarrega as listas */
  const handleDealLink = async (dealId: string, unlink: boolean) => {
    await dealLink.mutateAsync({ dealId, vehicleId, unlink });
    setSelectedDealId(null);
    await refetch();
  };

  /** Vincula/desvincula uma tarefa e recarrega as listas */
  const handleTaskLink = async (taskId: string, unlink: boolean) => {
    await taskLink.mutateAsync({ taskId, vehicleId, unlink });
    setSelectedTaskId(null);
    await refetch();
  };

  const isBusy = dealLink.isPending || taskLink.isPending;

  return (
    <>
      <Separator />

      <FormSection icon={Link2} title="Vínculos" hint="(aplicados na hora)">
        <InlineNotice variant="info">
          Os vínculos são independentes dos dados cadastrais: alterar e salvar o veículo não
          remove negócios, tarefas ou contratos já vinculados.
        </InlineNotice>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* ================= Negócios ================= */}
            {canViewDeals && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  Negócios
                  <span className="text-xs text-muted-foreground">({deals.length})</span>
                </div>

                {deals.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhum negócio vinculado.</p>
                ) : (
                  <ul className="divide-y rounded-md border">
                    {deals.map((deal) => (
                      <li key={deal.id} className="flex items-center gap-2 px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">{deal.title}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {[deal.phase_name, formatCurrency(deal.value)]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 shrink-0 text-destructive hover:text-destructive"
                          disabled={isBusy}
                          onClick={() => void handleDealLink(deal.id, true)}
                        >
                          <Unlink className="h-4 w-4" />
                          <span className="ml-1.5 hidden sm:inline">Desvincular</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex flex-col gap-2 sm:flex-row">
                  <DealSelector
                    value={selectedDealId}
                    onSelect={setSelectedDealId}
                    placeholder="Selecionar negócio para vincular..."
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 shrink-0"
                    disabled={
                      !selectedDealId || linkedDealIds.has(selectedDealId ?? '') || isBusy
                    }
                    onClick={() => selectedDealId && void handleDealLink(selectedDealId, false)}
                  >
                    {dealLink.isPending ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : (
                      <Link2 className="mr-1.5 h-4 w-4" />
                    )}
                    Vincular
                  </Button>
                </div>
              </div>
            )}

            {/* ================= Tarefas ================= */}
            {canViewTasks && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CheckSquare className="h-4 w-4 text-muted-foreground" />
                  Tarefas
                  <span className="text-xs text-muted-foreground">({tasks.length})</span>
                </div>

                {tasks.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhuma tarefa vinculada.</p>
                ) : (
                  <ul className="divide-y rounded-md border">
                    {tasks.map((task) => (
                      <li key={task.id} className="flex items-center gap-2 px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">
                            <span className="font-mono text-xs text-muted-foreground">
                              #{task.number}
                            </span>{' '}
                            {task.title}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {[task.status, task.assigned_name].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 shrink-0 text-destructive hover:text-destructive"
                          disabled={isBusy}
                          onClick={() => void handleTaskLink(task.id, true)}
                        >
                          <Unlink className="h-4 w-4" />
                          <span className="ml-1.5 hidden sm:inline">Desvincular</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex flex-col gap-2 sm:flex-row">
                  <ParentTaskSelector
                    value={selectedTaskId}
                    onSelect={setSelectedTaskId}
                    placeholder="Selecionar tarefa para vincular..."
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 shrink-0"
                    disabled={
                      !selectedTaskId || linkedTaskIds.has(selectedTaskId ?? '') || isBusy
                    }
                    onClick={() => selectedTaskId && void handleTaskLink(selectedTaskId, false)}
                  >
                    {taskLink.isPending ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : (
                      <Link2 className="mr-1.5 h-4 w-4" />
                    )}
                    Vincular
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </FormSection>
    </>
  );
}
